"use client";

import * as React from "react";

type VatReportRow = {
  transactionType: "SALE" | "PURCHASE" | "UNKNOWN";
  vatCase: "INLAND" | "IMPORT" | "EXPORT" | "EXEMPT" | "OPTION" | "UNKNOWN";
  vatTaxPoint: "INVOICE" | "PAYMENT" | "NONE" | "UNKNOWN";
  countEntries: number;
  grossAmount: number;
  vatAmount: number;
  netAmount: number;
};

type VatPeriodReport = {
  fromDate: string;
  toDate: string;
  reportingCurrency: "CHF" | "EUR" | "USD" | "GBP";
  rows: VatReportRow[];
  totals: {
    gross: number;
    vat: number;
    net: number;
  };
  warnings: string[];
};

function fmt(n: number, currency: "CHF" | "EUR" | "USD" | "GBP"): string {
  return `${currency} ${n.toFixed(2)}`;
}

export default function VatReportScreen() {
  const now = new Date();
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
  const today = now.toISOString().slice(0, 10);

  const [fromDate, setFromDate] = React.useState(first);
  const [toDate, setToDate] = React.useState(today);
  const [reportingCurrency, setReportingCurrency] = React.useState<"CHF" | "EUR" | "USD" | "GBP">("CHF");
  const [busy, setBusy] = React.useState(false);
  const [report, setReport] = React.useState<VatPeriodReport | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const internalFormUrl = `/api/vat/internal-form?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;

  async function load() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/vat/report?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}&reportingCurrency=${encodeURIComponent(reportingCurrency)}`,
        { cache: "no-store" }
      );
      const data = (await res.json()) as { ok?: boolean; error?: string; report?: VatPeriodReport };
      if (!res.ok || !data.ok || !data.report) throw new Error(data.error || "MWST-Report konnte nicht geladen werden");
      setReport(data.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
      setReport(null);
    } finally {
      setBusy(false);
    }
  }

  React.useEffect(() => {
    load();
  }, []);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>MWST-Periodenreport</h1>
          <p>Auswertung nach Steuerpunkt und MWST-Fall (Inland / Import / Export) für die Deklarationsvorbereitung.</p>
        </div>
        <div className="page-actions">
          <input className="input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <input className="input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          <select className="select" value={reportingCurrency} onChange={(e) => setReportingCurrency(e.target.value as "CHF" | "EUR" | "USD" | "GBP")}>
            <option value="CHF">CHF</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
          <a className="btn" href={internalFormUrl} target="_blank" rel="noreferrer">
            Internes Formular
          </a>
          <button className="btn btn-primary" type="button" onClick={load} disabled={busy}>
            {busy ? "Lädt..." : "Neu berechnen"}
          </button>
        </div>
      </div>

      {error && <div style={{ color: "#b91c1c", marginBottom: 10 }}>{error}</div>}

      <div className="table-wrap" style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span className="pill pill-final">Internes Formular aktiv</span>
          <span style={{ color: "var(--muted)", fontSize: 12 }}>
            XML-Export und XML-Import sind aus diesem Bereich entfernt und werden später unter "Nützliche Features" geführt.
          </span>
        </div>
      </div>

      {report && (
        <>
          <div className="table-wrap" style={{ padding: 14, marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div><strong>Zeitraum:</strong> {report.fromDate} bis {report.toDate}</div>
              <div><strong>Report-Währung:</strong> {report.reportingCurrency}</div>
              <div><strong>Gross:</strong> {fmt(report.totals.gross, report.reportingCurrency)}</div>
              <div><strong>MWST:</strong> {fmt(report.totals.vat, report.reportingCurrency)}</div>
              <div><strong>Netto:</strong> {fmt(report.totals.net, report.reportingCurrency)}</div>
            </div>
          </div>

          {report.warnings.length > 0 && (
            <div className="table-wrap" style={{ padding: 14, marginBottom: 12 }}>
              {report.warnings.map((w) => (
                <div key={w} className="pill pill-provisional" style={{ marginRight: 8 }}>{w}</div>
              ))}
            </div>
          )}

          <div className="table-wrap" style={{ padding: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Transaktion</th>
                  <th>MWST-Fall</th>
                  <th>Steuerpunkt</th>
                  <th>Anzahl</th>
                  <th>Gross</th>
                  <th>MWST</th>
                  <th>Netto</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 12, color: "var(--muted)" }}>
                      Keine Daten in der gewählten Periode.
                    </td>
                  </tr>
                ) : (
                  report.rows.map((r, i) => (
                    <tr key={`${r.transactionType}-${r.vatCase}-${r.vatTaxPoint}-${i}`}>
                      <td>{r.transactionType}</td>
                      <td>{r.vatCase}</td>
                      <td>{r.vatTaxPoint}</td>
                      <td>{r.countEntries}</td>
                      <td>{fmt(r.grossAmount, report.reportingCurrency)}</td>
                      <td>{fmt(r.vatAmount, report.reportingCurrency)}</td>
                      <td>{fmt(r.netAmount, report.reportingCurrency)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
